import SuperCarersClient from "clients/SuperCarersClient";
import SegmentClient from "clients/SegmentClient";
import UnbounceClient from "clients/UnbounceClient";
import SentryClient from "clients/SentryClient";
import LocalOperations from "logic/LocalOperations";

class SuperCarersOperations {
  static async getPostcodeDetail(postcode) {
    const outcodeOnly = postcode.trim().length <= 4;
    const url = outcodeOnly
      ? `/postcode/outcode/validate/${postcode}/`
      : `/postcode/validate/${postcode}/`;

    const response = await SuperCarersClient.get(url);

    return response.data.data;
  }

  static async checkPostcodeForCarers(
    postcode,
    distance = null,
    livein = false
  ) {
    const params = {
      archived: false,
      compliant: true,
      objects: true,
      per_page: window.POSTCODE_CARER_RESULTS_TOTAL,
      postcode,
      state: "approved",
      work_state: "available"
    };

    if (distance) {
      params.distance = distance;
    }

    if (livein) {
      params.care_type = [
        "liveincareweekendsonly",
        "liveincareanytime",
        "liveincareoccasionalonly"
      ];
    }

    const response = await SuperCarersClient.get("/search/carer/", params);

    return {
      carers: response.data.data.objects,
      carersTotal: response.data.data.total,
      liveinOnly: livein
    };
  }

  static async getAvailableCarers(
    postcode,
    careTypes = undefined,
    distance = undefined
  ) {
    const filters = {
      postcode,
      state: ["approved"]
    };

    if (careTypes) {
      filters.care_type = careTypes;
    }

    if (distance) {
      filters.distance = distance;
    }

    const response = await SuperCarersClient.get(
      "/carer/available/careneed/",
      filters
    );

    // count of available carers
    return response.data.data.carers;
  }

  static async fetchTaxonomies() {
    const response = await SuperCarersClient.get("/taxonomy/");
    return response.data.data;
  }

  static async assignDiscountCode(userId, discountCode) {
    const response = await SuperCarersClient.put(
      `/discount/${discountCode}/assign/${userId}/`
    );
    return response.data.data;
  }

  static async validateDiscountCode(discountCode) {
    const response = await SuperCarersClient.get(`/discount/${discountCode}/`);
    return response.data.data;
  }

  static async validatePhoneNumber(phonenumber) {
    const response = await SuperCarersClient.get(
      `/phonenumber/validate/${phonenumber}/`
    );
    return response.data.data;
  }

  static async createCustomer({
    newsLetterSignup = undefined,
    agencyInterest = undefined,
    agencyPermission = undefined,
    ...customerData
  }) {
    const tracking = this.getTrackingData({
      newsLetterSignup,
      agencyInterest,
      agencyPermission
    });

    // eslint-disable-next-line camelcase
    const { firstname, lastname, email, phone_number } = customerData;

    let response;
    try {
      response = await SuperCarersClient.post("/user/customer/", {
        ...customerData,
        tracking: { ...customerData.tracking, ...tracking }
      });
    } catch (e) {
      this.captureSignupError(e, firstname, lastname, email);
      throw e;
    }

    const customer = response.data.data;

    SegmentClient.trackIdentity(customer.id, {
      createdAt: customer.created_at,
      email,
      firstname,
      lastname,
      phone_number
    });
    UnbounceClient.trackConversion();

    return customer;
  }

  static async updateCustomer(
    userId,
    {
      newsLetterSignup = undefined,
      agencyInterest = undefined,
      agencyPermission = undefined,
      ...customerData
    }
  ) {
    const tracking = this.getTrackingData({
      newsLetterSignup,
      agencyInterest,
      agencyPermission
    });
    const { firstname, lastname, email } = customerData;

    let response;
    try {
      response = await SuperCarersClient.put(`/user/${userId}/`, {
        tracking,
        ...customerData
      });
    } catch (e) {
      this.captureSignupError(e, firstname, lastname, email);
      throw e;
    }

    const customer = response.data.data;

    SegmentClient.trackIdentity(customer.id, {
      createdAt: customer.created_at,
      ...customerData
    });

    return customer;
  }

  static getTrackingData({
    newsLetterSignup = undefined,
    agencyInterest = undefined,
    agencyPermission = undefined
  }) {
    const tracking = {
      _ga: LocalOperations.getGoogleAnalyticsCookie(),
      gclid:
        LocalOperations.getGclidFromUrl() || LocalOperations.getGclidCookie(),
      ...LocalOperations.getUtmTags()
    };

    if (newsLetterSignup === true) {
      tracking.newsLetterSignup = "true";
    }

    if (agencyInterest !== undefined) {
      tracking.agencyInterest = agencyInterest.toString();
    }

    if (agencyPermission !== undefined) {
      tracking.agencyPermission = agencyPermission.toString();
    }

    return tracking;
  }

  static captureSignupError(e, firstname, lastname, email) {
    if (e.response && e.response.status === 409) {
      // duplicate email address
      SentryClient.captureEvent({
        message: `Someone attempted to sign up again with the email ${email}.`,
        stacktrace: e,
        level: "error"
      });
    } else {
      SentryClient.captureEvent({
        message: `${firstname} ${lastname} was unable to sign up due to a server error.`,
        stacktrace: e,
        level: "error"
      });
    }
  }

  static async createNeed(needData) {
    const response = await SuperCarersClient.post("/care/need/", needData);
    return response.data.data;
  }

  static async updateNeed(needId, needData) {
    const response = await SuperCarersClient.put(
      `/care/need/${needId}/`,
      needData
    );
    return response.data.data;
  }

  static async addVitalityMember(email, membershipNumber) {
    const response = await SuperCarersClient.post("/vitality/", {
      email,
      membership_number: membershipNumber
    });
    return response.data;
  }
}

export default SuperCarersOperations;
